import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import {
  Building2, Package, Wallet, MessageSquare, Smartphone, Sparkles, Users, Plug, ServerCog,
  Search, Check, AlertCircle, ChevronRight, ArrowLeft,
} from 'lucide-react';
import { settingsCategories } from '@/pages/MainSettingsPage';
import SettingsPage from '@/pages/SettingsPage';
import { EMBEDDABLE_PAGES } from '@/pages/SettingsLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/settings`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Jede Karte: Titel, Beschreibung, Ziel-Route (bestehende Seite), optionaler statusKey.
const AREAS = [
  {
    id: 'unternehmen', label: 'Unternehmen', icon: Building2, superadmin: false,
    desc: 'Firmendaten, Standorte, Öffnungszeiten',
    cards: [
      { title: 'Studio-Informationen', desc: 'Name, Logo, Kontaktdaten', to: '/settings/studio-info' },
      { title: 'Studio-Verwaltung', desc: 'Standorte und Räume', to: '/studios' },
      { title: 'Öffnungszeiten & Kalender', desc: 'Globaler Standard + Buchungsregeln', to: '/settings/oeffnungszeiten' },
      { title: 'Kalenderspalten', desc: 'Spalten per Drag-&-Drop ordnen & aus-/einblenden', to: '/settings/calendar-columns' },
      { title: 'Termin-Menü (Rechtsklick)', desc: 'Aktionen im Kalender-Kontextmenü', to: '/settings/calendar-menu' },
      { title: 'Öffnungszeiten (Standorte)', desc: 'Abweichende Zeiten & Feiertage je Studio', to: '/studios/hours' },
      { title: 'Feiertage & Ferien', desc: 'Schliesstage', to: '/settings/holidays' },
      { title: 'Standort-Bonus', desc: 'Boni pro Standort', to: '/studios/bonus' },
    ],
  },
  {
    id: 'angebot', label: 'Angebot', icon: Package, superadmin: false,
    desc: 'Services, Produkte, Ressourcen, Buchungsregeln',
    cards: [
      { title: 'Dienstleistungen', desc: 'Leistungen & Preise', to: '/services' },
      { title: 'Kategorien', desc: 'Leistungskategorien', to: '/settings/kategorien' },
      { title: 'Zusatzleistungen', desc: 'Add-ons & Puffer', to: '/settings/zusatzleistungen' },
      { title: 'Pakete & Produkte', desc: 'Shop-Produkte', to: '/products' },
      { title: 'Ressourcen', desc: 'Räume & Geräte', to: '/resources' },
      { title: 'Buchungsregeln (Policies)', desc: 'Stornofristen, Regeln', to: '/settings/buchungsregeln' },
      { title: 'Zeitplaner', desc: 'Slot-Planung', to: '/settings/zeitplaner' },
    ],
  },
  {
    id: 'finanzen', label: 'Zahlungen & Finanzen', icon: Wallet, superadmin: false,
    desc: 'Zahlungsanbieter, Karten, MWST, Rechnungen',
    cards: [
      { title: 'Online-Zahlungen (Payrexx)', desc: 'Payrexx-Anbindung', to: '/settings/zahlungen', statusKey: 'payrexx' },
      { title: 'Zahlungsarten vor Ort', desc: 'Bar, Karte, TWINT', to: '/settings/payment-methods' },
      { title: 'SumUp Kartenleser', desc: 'SumUp Solo', to: '/settings/sumup', statusKey: 'sumup' },
      { title: 'Hinterlegte Karten', desc: 'Gespeicherte Karten', to: '/cards-overview' },
      { title: 'Kartenablauf', desc: 'Ablauf-Erinnerungen', to: '/settings/card-expiry' },
      { title: 'MWST', desc: 'Steuersätze', to: '/settings/mwst' },
      { title: 'Rechnungs- & Firmendaten', desc: 'Rechnungslayout', to: '/settings/rechnungsdaten' },
      { title: 'Automatische Abrechnung', desc: 'Abo-Abrechnung', to: '/settings/auto-abrechnung' },
      { title: 'Sammel-Zahlungsaufforderung', desc: 'Mehrere offene Trainings', to: '/settings/bundle-reminders' },
      { title: 'Tarif 595', desc: 'Krankenkassen-Tarif', to: '/settings/tarif-595' },
      { title: 'Physio-Abrechnung (311/590)', desc: 'MediData-Abrechnung', to: '/settings/physio-billing' },
      { title: 'ePost Briefversand', desc: 'Rechnungen per Post', to: '/settings/epost' },
    ],
  },
  {
    id: 'kommunikation', label: 'Kommunikation', icon: MessageSquare, superadmin: false,
    desc: 'Kanäle, Vorlagen, Regeln, Protokoll',
    cards: [
      { title: 'Kanäle', desc: 'E-Mail, WhatsApp, SMS, Push', to: '/settings/kanaele', statusKey: 'email' },
      { title: 'WhatsApp', desc: 'Nummer & Bot', to: '/whatsapp', statusKey: 'whatsapp' },
      { title: 'WhatsApp-Vorlagen', desc: 'Templates', to: '/whatsapp/templates' },
      { title: 'E-Mail-Vorlagen', desc: 'Mail-Templates', to: '/email/templates' },
      { title: 'Push-Texte', desc: 'Push-Nachrichten', to: '/push/configs' },
      { title: 'Erinnerungen & Vorlagen', desc: 'Termin-Erinnerungen', to: '/settings/kommunikation' },
      { title: 'Automatisierung', desc: 'Regeln & Trigger', to: '/automation/rules' },
      { title: 'Re-Engagement', desc: 'Win-Back-Kampagnen', to: '/settings/re-engagement' },
      { title: 'Google Bewertungen', desc: 'Review-Anfragen', to: '/settings/google-bewertungen' },
      { title: 'Protokoll: E-Mail', desc: 'Versand-Logs', to: '/email/logs' },
      { title: 'Protokoll: WhatsApp', desc: 'Nachrichten-Logs', to: '/whatsapp/logs' },
    ],
  },
  {
    id: 'portal', label: 'Kundenportal & App', icon: Smartphone, superadmin: false,
    desc: 'Aussehen, Inhalt, Widget, Login, App',
    cards: [
      { title: 'Portal-Übersicht', desc: 'Portal-Einstellungen', to: '/portal-settings' },
      { title: 'Aussehen', desc: 'Farben & Logo', to: '/portal-settings/appearance' },
      { title: 'Inhalt', desc: 'Texte & Seiten', to: '/portal-settings/content' },
      { title: 'Buchung', desc: 'Buchungsablauf', to: '/portal-settings/booking' },
      { title: 'Einbettung / Widget', desc: 'iFrame & Link', to: '/portal-settings/embed' },
      { title: 'Tracking & Rewards', desc: 'Punkte & Tracking', to: '/portal-settings/tracking-rewards' },
      { title: 'Krankenkassenformulare', desc: 'Versicherung', to: '/portal-settings/insurance' },
      { title: 'Google Login', desc: 'OAuth-Anmeldung', to: '/portal-settings/google-oauth' },
      { title: 'Mobile App', desc: 'App-Konfiguration', to: '/mobile-app' },
    ],
  },
  {
    id: 'ki', label: 'KI', icon: Sparkles, superadmin: false,
    desc: 'Provider, Modelle, Chatbot, Verbrauch',
    cards: [
      { title: 'KI & API', desc: 'Provider, Keys, Modelle, Chatbot', to: '/settings/ki', statusKey: 'ai' },
      { title: 'Ernährungs-KI', desc: 'Health Coach', to: '/settings/ernaehrung' },
      { title: 'KI-Guthaben', desc: 'Verbrauch & Zukauf', to: '/admin/ai-budget' },
    ],
  },
  {
    id: 'team', label: 'Team & Zugriff', icon: Users, superadmin: false,
    desc: 'Mitarbeiter, Rollen, Zeiterfassung, Lohn',
    cards: [
      { title: 'Mitarbeiter', desc: 'Team & Rollen', to: '/personal' },
      { title: 'Stundenerfassung', desc: 'Arbeitszeiten', to: '/personal?tab=stunden' },
      { title: 'Lohn & AHV', desc: 'Lohnabrechnung', to: '/personal?tab=lohn' },
      { title: 'Änderungsprotokoll', desc: 'Wer hat was geändert', to: '/settings/aenderungsprotokoll' },
    ],
  },
  {
    id: 'integrationen', label: 'Integrationen', icon: Plug, superadmin: false,
    desc: 'Verbundene Systeme',
    cards: [
      { title: 'Stripe', desc: 'Kartenzahlung', to: '/settings/zahlungen', statusKey: 'stripe' },
      { title: 'Worldline', desc: 'Terminal-Zahlung', to: '/settings/payment-methods', statusKey: 'worldline' },
      { title: 'Mindbody', desc: 'Datenimport', to: '/admin/integrations/mindbody' },
      { title: 'Google Business', desc: 'Öffnungszeiten-Sync', to: '/admin/integrations/google-business' },
      { title: 'InBody', desc: 'Körperdaten', to: '/settings/inbody' },
      { title: 'ERP / Buchhaltung', desc: 'Webhook-Anbindung', to: '/settings/erp' },
      { title: 'Meta Ads', desc: 'Werbe-Anbindung', to: '/admin/ads' },
      { title: 'iCloud-Kontakte', desc: 'CardDAV-Sync', to: '/portal-settings/icloud-sync' },
    ],
  },
  {
    id: 'system', label: 'System', icon: ServerCog, superadmin: true,
    desc: 'Nur Superadmin: Backup, Wartung, Diagnose',
    cards: [
      { title: 'Backup & Export', desc: 'Datensicherung', to: '/settings/backup' },
      { title: 'Wartung', desc: 'Wartungsmodus', to: '/settings/advanced?card=maintenance' },
      { title: 'Mobile Monitor', desc: 'App-Gesundheit', to: '/mobile-health' },
      { title: 'Mobile Errors', desc: 'App-Fehler', to: '/mobile-errors' },
      { title: 'System Health', desc: 'Systemzustand', to: '/system-health' },
      { title: 'DB Index-Stats', desc: 'Datenbank-Indizes', to: '/admin/index-stats' },
      { title: 'Audit-Log', desc: 'Änderungsprotokoll', to: '/audit-log' },
    ],
  },
];

// Übersetzt die "freundlichen" Karten-IDs des Hubs auf die kanonischen IDs in
// settingsCategories (MainSettingsPage), die die eingebetteten Panels auflösen.
const FRIENDLY_TO_CANONICAL = {
  'studio-info': 'general',
  'oeffnungszeiten': 'availability',
  'kategorien': 'categories',
  'zusatzleistungen': 'addons',
  'buchungsregeln': 'policies',
  'zeitplaner': 'scheduler',
  'mwst': 'tax',
  'rechnungsdaten': 'invoice',
  'auto-abrechnung': 'billing-auto',
  'tarif-595': 'tarif595',
  'kommunikation': 'communication',
  'google-bewertungen': 'google-review',
  'ernaehrung': 'nutrition',
  'card-expiry': 'card_expiry',
  're-engagement': 'reengagement',
};

// Lookup kanonischer Karten (id -> card) aus settingsCategories
const CAT_BY_ID = {};
settingsCategories.forEach((cat) => cat.cards.forEach((c) => { CAT_BY_ID[c.id] = { ...c, section: cat.title }; }));

// Löst ein Hub-Panel (panelId aus /settings/:panelId) auf eine eingebettete
// Komponente ODER eine Standalone-Route auf.
function resolvePanel(panelId) {
  const canonical = FRIENDLY_TO_CANONICAL[panelId] || panelId;
  const card = CAT_BY_ID[canonical] || null;
  const entry = EMBEDDABLE_PAGES[canonical];
  const Component = entry ? (entry.component || entry) : null;
  const componentProps = entry && entry.props ? entry.props : null;
  let forcedCard = null;
  if (!Component && card && card.path) {
    const m = card.path.match(/\/settings\/advanced\?(?:tab|card)=([\w-]+)/);
    if (m) forcedCard = m[1];
  }
  const embeddable = !!Component || !!forcedCard;
  const standalonePath = (!embeddable && card && card.path && !card.path.startsWith('/settings/advanced')) ? card.path : null;
  return { canonical, card, Component, componentProps, forcedCard, embeddable, standalonePath };
}

// Findet den Hub-Bereich + die Karte zu einer /settings/:panelId-Route.
function findAreaCard(panelId) {
  const target = `/settings/${panelId}`;
  for (const a of AREAS) {
    const c = a.cards.find((cc) => cc.to === target);
    if (c) return { area: a, card: c };
  }
  return null;
}

const StatusBadge = ({ status }) => {
  if (status === 'configured') return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700" data-testid="status-configured"><Check className="h-3 w-3" /> Konfiguriert</span>;
  if (status === 'missing') return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700" data-testid="status-missing"><AlertCircle className="h-3 w-3" /> Fehlt</span>;
  if (status === 'error') return <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700"><AlertCircle className="h-3 w-3" /> Fehler</span>;
  return null;
};

export default function SettingsHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { panelId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuper = user?.role === 'superadmin';
  const areas = useMemo(() => AREAS.filter((a) => !a.superadmin || isSuper), [isSuper]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState({});

  useEffect(() => {
    axios.get(`${API}/status`, auth()).then((r) => setStatus(r.data || {})).catch(() => {});
  }, []);

  // Phase 3: Alte Routen weich umleiten
  const panelRedirect = panelId === 'ki-api' ? '/settings/ki' : (panelId === 'colors' ? '/settings/oeffnungszeiten' : null);

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;

  const results = useMemo(() => {
    if (!searching) return [];
    const out = [];
    areas.forEach((a) => a.cards.forEach((c) => {
      if (c.title.toLowerCase().includes(q) || (c.desc || '').toLowerCase().includes(q) || a.label.toLowerCase().includes(q)) {
        out.push({ ...c, area: a.label });
      }
    }));
    return out;
  }, [q, searching, areas]);

  // Aktiver Bereich: im Panel-Modus der Bereich, der das Panel enthält; sonst aus ?area= / erster.
  const areaCard = panelId ? findAreaCard(panelId) : null;
  const activeId = (areaCard?.area?.id) || searchParams.get('area') || areas[0]?.id;
  const activeArea = areas.find((a) => a.id === activeId) || areas[0];

  const gotoArea = (id) => {
    if (panelId) navigate(`/settings?area=${id}`);
    else { setSearch(''); setSearchParams(id === areas[0]?.id ? {} : { area: id }); }
  };

  const Card = ({ c }) => (
    <button onClick={() => navigate(c.to)} data-testid={`settings-card-${c.to.replace(/[^a-z0-9]/gi, '-')}`}
      className="group flex items-start justify-between rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition-all hover:border-blue-400 hover:shadow">
      <div className="pr-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{c.title}</span>
          {c.statusKey && <StatusBadge status={status[c.statusKey]} />}
        </div>
        <div className="mt-0.5 text-xs text-gray-500">{c.desc}{c.area && <span className="ml-1 text-gray-400">· {c.area}</span>}</div>
      </div>
      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-300 group-hover:text-blue-500" />
    </button>
  );

  const LeftNav = () => (
    <nav className="shrink-0 md:w-60" data-testid="settings-nav">
      <ul className="space-y-1">
        {areas.map((a) => {
          const Icon = a.icon;
          const on = !searching && a.id === activeArea.id;
          return (
            <li key={a.id}>
              <button onClick={() => gotoArea(a.id)} data-testid={`settings-nav-${a.id}`}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${on ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                <Icon className={`h-4 w-4 ${on ? 'text-white' : 'text-gray-400'}`} />
                <span className="font-medium">{a.label}</span>
                {a.superadmin && <span className={`ml-auto rounded px-1.5 py-0.5 text-[10px] ${on ? 'bg-white/20' : 'bg-gray-200 text-gray-500'}`}>Superadmin</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );

  const Shell = ({ children }) => (
    <div className="mx-auto max-w-6xl p-6" data-testid="settings-hub">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Einstellungen</h1>
        {!panelId && (
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Einstellungen durchsuchen…"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="settings-search" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-6 md:flex-row">
        <LeftNav />
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );

  // ===== Panel-Modus: /settings/:panelId im neuen Design einbetten =====
  if (panelId) {
    if (panelRedirect) return <Navigate to={panelRedirect} replace />;
    const res = resolvePanel(panelId);
    if (!res.embeddable) {
      if (res.standalonePath) return <Navigate to={res.standalonePath} replace />;
      return <Navigate to="/settings" replace />;
    }
    const title = areaCard?.card?.title || res.card?.title || 'Einstellung';
    const desc = areaCard?.card?.desc || res.card?.description || '';
    const backArea = areaCard?.area?.id || areas[0]?.id;
    return (
      <Shell>
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-500" data-testid="settings-breadcrumb">
          <button onClick={() => navigate(`/settings?area=${backArea}`)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-gray-600 hover:bg-gray-100" data-testid="settings-back">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </button>
          <span className="text-gray-300">/</span>
          <span>{areaCard?.area?.label || 'Einstellungen'}</span>
          <span className="text-gray-300">/</span>
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6" data-testid="settings-panel">
          <div className="mb-4 border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {desc && <p className="mt-0.5 text-sm text-gray-500">{desc}</p>}
          </div>
          <div className="settings-embedded-body">
            {res.Component
              ? React.createElement(res.Component, { embedded: true, ...(res.componentProps || {}) })
              : <SettingsPage embedded forcedCard={res.forcedCard} key={res.forcedCard} />}
          </div>
        </div>
      </Shell>
    );
  }

  // ===== Hub-Modus: Bereichs-Kacheln =====
  return (
    <Shell>
      {searching ? (
        <div data-testid="settings-search-results">
          <p className="mb-3 text-sm text-gray-500">{results.length} Treffer für „{search}“</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {results.map((c, i) => <Card key={i} c={c} />)}
          </div>
          {results.length === 0 && <p className="text-sm text-gray-400">Keine Einstellung gefunden.</p>}
        </div>
      ) : (
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{activeArea.label}</h2>
            <p className="text-sm text-gray-500">{activeArea.desc}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {activeArea.cards.map((c, i) => <Card key={i} c={c} />)}
          </div>
        </div>
      )}
    </Shell>
  );
}
